"use client";

import * as React from "react";
import { useTranslations } from "next-intl";

import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useInterfaceLanguageOptions } from "@/hooks/use-ui-language-options";
import { User } from "@/types/user";

type Props = {
  user: User;
  onChange: (next: User) => void;
  showErrors: boolean;
  selectedLanguage: string;
  onLanguageChange: (lang: string) => void;
};

/**
 * Profile form fields: lastname / firstname / patronymic (editable),
 * phone + email (read-only), language selector.
 */
const UserProfileFields: React.FC<Props> = ({
  user,
  onChange,
  showErrors,
  selectedLanguage,
  onLanguageChange,
}) => {
  const t = useTranslations();
  const languageOptions = useInterfaceLanguageOptions();

  return (
    <div className="space-y-4">
      {/* Фамилия */}
      <div>
        <label className="text-sm font-medium mb-1 block">{t("label-lastname")}</label>
        <Input
          value={user.lastname ?? ""}
          onChange={(e) => onChange({ ...user, lastname: e.target.value })}
          placeholder={t("label-lastname")}
          aria-invalid={showErrors && !(user.lastname ?? "").toString().trim()}
        />
      </div>

      {/* Имя */}
      <div>
        <label className="text-sm font-medium mb-1 block">{t("label-firstname")}</label>
        <Input
          value={user.firstname ?? ""}
          onChange={(e) => onChange({ ...user, firstname: e.target.value })}
          placeholder={t("label-firstname")}
          aria-invalid={showErrors && !(user.firstname ?? "").toString().trim()}
        />
      </div>

      {/* Отчество */}
      <div>
        <label className="text-sm font-medium mb-1 block">{t("label-patronymic")}</label>
        <Input
          value={user.patronymic ?? ""}
          onChange={(e) => onChange({ ...user, patronymic: e.target.value })}
          placeholder={t("label-patronymic")}
        />
      </div>

      {/* Телефон (read-only) */}
      <div className="flex items-center gap-3">
        <PhoneInput
          value={(user.phone ?? "").toString()}
          onChange={() => { }}
          international
          disabled
          className="bg-gray-100 cursor-not-allowed opacity-70"
        />
        <span className="text-sm text-muted-foreground">{t("label-mobile")}</span>
      </div>

      {/* Email (read-only) */}
      <div>
        <label className="text-sm font-medium mb-1 block">Email</label>
        <Input
          type="email"
          inputMode="email"
          autoComplete="email"
          value={user.email ?? ""}
          onChange={() => { }}
          placeholder="Email"
          disabled
          readOnly
          className="bg-gray-100 cursor-not-allowed opacity-70"
        />
      </div>

      {/* Язык */}
      <div>
        <label className="text-sm font-medium mb-1 block">{t("label-language")}</label>
        <Select value={selectedLanguage} onValueChange={onLanguageChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t("label-language")} />
          </SelectTrigger>
          <SelectContent>
            {languageOptions.map((option) => (
              <SelectItem key={option.locale} value={option.locale}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default UserProfileFields;
